/* eslint-disable react-hooks/exhaustive-deps */
import { useMediaQuery, Grid, Box, useTheme, Typography, Button, Container, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CustomRightImage from "@components/CustomRightImagev2";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { useEffect, useState } from "react";
import { GuaranteeType, TaxSystemConfigResponse } from "@interfaces/index";
import {
  amountToText,
  calculateAmountMonthly,
  formatCurrency,
  generateQuotationAmountMarks,
  generateQuotationTermMarks,
} from "@helpers/funciones";
import { StyledSlider } from "@components/CustomSlider";
import ButtonTypeSelectedV2 from "@components/ButtonTypeSelectedV2";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { useLazyGetGuaranteeConfigQuery } from "@api/catalogs";

const DEFAULT_CONFIG = {
  id: "",
  code: "",
  product_id: "",
  minAmount: 0,
  maxAmount: 0,
  stepAmount: 0,
  minTerm: 0,
  maxTerm: 0,
  stepTerm: 0,
  defaultAmount: 0,
  defaultTerm: 0,
  tax_system_type: "",
};

const CotizadorGeneric = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();

  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [getGuaranteeTypeConfig] = useLazyGetGuaranteeConfigQuery();

  // variables para almacenar configuraciones de api
  const [configPM, setConfigPM] = useState<TaxSystemConfigResponse | null>(null);
  const [configPFAE, setConfigPFAE] = useState<TaxSystemConfigResponse | null>(null);
  const [guaranteeListPM, setGuaranteeListPM] = useState<GuaranteeType[]>([]);
  const [guaranteeListPFAE, setGuaranteeListPFAE] = useState<GuaranteeType[]>([]);

  // variables para mostrar selectores
  const [showRegimeSelector, setShowRegimeSelector] = useState<boolean>(false);
  const [showGuaranteeSelector, setShowGuaranteeSelector] = useState<boolean>(false);
  const [amountMarks, setAmountMarks] = useState<{ value: number; label: string }[]>([]);
  const [termMarks, setTermMarks] = useState<{ value: number; label: string }[]>([]);

  // configuraciones seleccionadas
  const [cotizadorConfig, setCotizadorConfig] = useState<TaxSystemConfigResponse>(DEFAULT_CONFIG);
  const [guaranteeList, setGuaranteeList] = useState<GuaranteeType[]>([]);
  const [personType, setPersonType] = useState<string>("PM");
  const [guaranteeType, setGuaranteeType] = useState<string>("");
  const [selectedAmount, setSelectedAmount] = useState<number>(cotizadorConfig.defaultAmount);
  const [selectedTerm, setSelectedTerm] = useState<number>(cotizadorConfig.defaultTerm);
  const [monthlyRate, setMonthlyRate] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const { type: cotizadorType, tax_system_codes, enable_guarantee } = interfaceConfig.cotizador || {};
      const { pm: pmCode, pfae: pfaeCode } = tax_system_codes || {};

      // validar que el tipo de cotizador sea valido
      if (cotizadorType !== "simple") {
        throw new Error("Cotizador type not supported");
      }

      // validar que se haya configurado almenos un regimen
      if (!pmCode || !pfaeCode) {
        throw new Error("PM or PFAE tax system code is required");
      }

      let list_guarantee_PM: GuaranteeType[] = [];
      let list_guarantee_PFAE: GuaranteeType[] = [];

      // obtener las configuraciones de los regimenes
      const response_PM = pmCode ? await getTaxSystemConfig(pmCode).unwrap() : null;
      const response_PFAE = pfaeCode ? await getTaxSystemConfig(pfaeCode).unwrap() : null;

      // si esta habilitado el flag de garantia, obtener los tipos de garantia
      if (enable_guarantee) {
        if (pmCode) {
          list_guarantee_PM = (await getGuaranteeTypeConfig({ tax_code: pmCode }).unwrap())
            .map((item) => item.guarantee_type)
            .sort((a, b) => a.name.localeCompare(b.name));

          // validar que se encontraron tipos de garantia para el regimen PM
          if (list_guarantee_PM.length === 0) {
            throw new Error("No se encontraron tipos de garantia para el regimen PM");
          }
        }

        if (pfaeCode) {
          list_guarantee_PFAE = (await getGuaranteeTypeConfig({ tax_code: pfaeCode }).unwrap())
            .map((item) => item.guarantee_type)
            .sort((a, b) => a.name.localeCompare(b.name));

          // validar que se encontraron tipos de garantia para el regimen PFAE
          if (list_guarantee_PFAE.length === 0) {
            throw new Error("No se encontraron tipos de garantia para el regimen PFAE");
          }
        }
      }

      setConfigPM(response_PM);
      setConfigPFAE(response_PFAE);
      setGuaranteeListPM(list_guarantee_PM);
      setGuaranteeListPFAE(list_guarantee_PFAE);
      setShowRegimeSelector(response_PM && response_PFAE ? true : false);
      setShowGuaranteeSelector(enable_guarantee ? true : false);
      setPersonType(response_PM ? "PM" : "PFAE");
    } catch (error) {
      console.error("Error fetching configurations", error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const continueToTOFU = () => {
    const { enable_guarantee } = interfaceConfig.cotizador || {};

    // obtener el source code de la url
    const sourceCode = sessionStorage.getItem("source_code");
    let guaranteeSelected: GuaranteeType | undefined;

    // si esta habilitado el flag de garantia, validar que se haya seleccionado un tipo de garantia
    if (enable_guarantee) {
      if (!setGuaranteeType) {
        return;
      }

      // obtener el tipo de garantia seleccionada
      guaranteeSelected = guaranteeList.find((item) => item.code === guaranteeType);

      // validar que se haya seleccionado un tipo de garantia
      if (!guaranteeSelected) {
        return;
      }
    }

    // validar que se haya seleccionado un producto, un monto y una tasa mensual
    if (!cotizadorConfig.product_id || !selectedAmount || !monthlyRate) {
      return;
    }

    // obtener el source code de la url

    // construir la url de redirecionamiento
    let urlPortal = `/producto/creditosimple/crear_cuenta?tax_system_id=${cotizadorConfig.id}`;
    urlPortal += `&product_id=${cotizadorConfig.product_id}&person_type=${cotizadorConfig.code}`;
    urlPortal += `&loan_amount=${selectedAmount}&loan_term=${selectedTerm}&tax_system_type=${cotizadorConfig.tax_system_type}`;
    urlPortal += guaranteeSelected
      ? `&guarantee_types=${guaranteeSelected.id}&guarantee_type_code=${guaranteeSelected.code}`
      : "";
    urlPortal += sourceCode ? `&source_code=${sourceCode}` : "";

    navigate(urlPortal);
  };

  useEffect(() => {
    if (personType === "PM" && configPM) {
      setCotizadorConfig(configPM);
      setGuaranteeList(guaranteeListPM);
      setGuaranteeType("");
    } else if (personType === "PFAE" && configPFAE) {
      setCotizadorConfig(configPFAE);
      setGuaranteeList(guaranteeListPFAE);
      setGuaranteeType("");
    }
  }, [personType, configPM, configPFAE]);

  useEffect(() => {
    const { minAmount, maxAmount, minTerm, maxTerm, stepTerm, defaultAmount, defaultTerm } = cotizadorConfig;
    const marksAmount = generateQuotationAmountMarks(minAmount, maxAmount);
    const marksTerm = generateQuotationTermMarks(minTerm, maxTerm, stepTerm);

    setAmountMarks(marksAmount);
    setTermMarks(marksTerm);
    setSelectedAmount(defaultAmount);
    setSelectedTerm(defaultTerm);
  }, [cotizadorConfig]);

  useEffect(() => {
    if (selectedAmount && selectedTerm) {
      const monthly = calculateAmountMonthly(selectedAmount, selectedTerm, interfaceConfig?.interest_rate);
      setMonthlyRate(monthly);
    }
  }, [selectedAmount, selectedTerm]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sourceCode = params.get("code");

    if (sourceCode) {
      sessionStorage.setItem("source_code", sourceCode);
    } else {
      sessionStorage.removeItem("source_code");
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <Container sx={{ height: "100%" }}>
          {isLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress size={40} />
            </Box>
          )}
          {!isLoading && hasError && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: 3,
                bgcolor: "#fff",
                px: 2,
              }}
            >
              <Typography variant="h1" fontWeight="700" color="#000">
                Oops!
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="#000">
                Ocurrió un error al procesar la información
              </Typography>
              <Button
                variant="contained"
                onClick={fetchData}
                sx={{
                  px: 3,
                  py: 1.5,
                }}
              >
                Reintentar
              </Button>
            </Box>
          )}
          {!isLoading && !hasError && (
            <Container maxWidth="sm">
              <Typography fontWeight={500} variant="h3" mt={4} color="primary.dark" textAlign={"center"}>
                Cotizador
              </Typography>

              <Box>
                {showRegimeSelector && (
                  <Box>
                    <Typography fontWeight={500} mt={1} color="primary.dark" textAlign={"center"}>
                      Selecciona tu régimen fiscal
                    </Typography>

                    <Grid container spacing={isMobileOrTablet ? 0 : 2} mt={1} mr={isMobileOrTablet ? 1 : 0}>
                      <Grid item xs={6} md={6} display="flex" justifyContent={isMobileOrTablet ? "center" : "flex-end"}>
                        <ButtonTypeSelectedV2
                          title="Persona Moral"
                          onClick={() => setPersonType("PM")}
                          isSelected={personType === "PM"}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        md={6}
                        display="flex"
                        justifyContent={isMobileOrTablet ? "center" : "flex-start"}
                      >
                        <ButtonTypeSelectedV2
                          title="Persona Física con Actividad Empresarial"
                          onClick={() => setPersonType("PFAE")}
                          isSelected={personType === "PFAE"}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {showGuaranteeSelector && (
                  <Box sx={{ mt: 2, border: "1px solid #000", borderRadius: 1, p: 2 }}>
                    <Typography fontWeight={500} mt={1} color="primary.dark" textAlign={"center"}>
                      Selecciona tu tipo de Garantía
                    </Typography>

                    <Box
                      sx={{
                        mt: 2,
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      {guaranteeList.map((guarantee) => (
                        <ButtonTypeSelectedV2
                          key={guarantee.code}
                          title={guarantee.name}
                          onClick={() => setGuaranteeType(guarantee.code)}
                          isSelected={guaranteeType === guarantee.code}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                <Box mt={1} px={isMobileOrTablet ? 3 : 1}>
                  <Typography fontWeight={500} color="primary.dark" textAlign={"center"}>
                    Selecciona el monto
                  </Typography>

                  <Typography fontWeight={700} variant="body2" color="primary.main" textAlign={"center"}>
                    {amountToText(selectedAmount)}
                  </Typography>

                  <StyledSlider
                    aria-label="amount"
                    min={cotizadorConfig.minAmount}
                    max={cotizadorConfig.maxAmount}
                    step={cotizadorConfig.stepAmount}
                    marks={amountMarks}
                    defaultValue={cotizadorConfig.defaultAmount}
                    value={selectedAmount}
                    onChange={(_event, newValue) => setSelectedAmount(newValue as number)}
                  />
                </Box>

                <Box mb={1} px={isMobileOrTablet ? 3 : 1}>
                  <Typography fontWeight={500} mb={2} color="primary.dark" textAlign={"center"}>
                    Selecciona el plazo en meses
                  </Typography>

                  <StyledSlider
                    aria-label="term"
                    min={cotizadorConfig.minTerm}
                    max={cotizadorConfig.maxTerm}
                    step={1}
                    defaultValue={cotizadorConfig.defaultTerm}
                    marks={termMarks}
                    value={selectedTerm}
                    onChange={(_event, newValue) => setSelectedTerm(newValue as number)}
                    valueLabelDisplay="on"
                  />
                </Box>

                <Typography fontWeight={700} color="primary.main" textAlign={"center"} mt={2}>
                  Pago mensual estimado*
                </Typography>

                <Typography
                  mb={2}
                  textAlign={"center"}
                  variant="h4"
                  fontWeight={theme?.palette?.fontWeights?.semibold}
                  color="primary.dark"
                >
                  {isNaN(monthlyRate) || !isFinite(monthlyRate) ? "0" : formatCurrency(monthlyRate)}{" "}
                  <Typography
                    variant="h5"
                    component={"span"}
                    fontWeight={theme?.palette?.fontWeights?.semibold}
                    color="primary.dark"
                  >
                    MXN
                  </Typography>
                </Typography>

                <Typography
                  textAlign={"center"}
                  fontWeight={theme?.palette?.fontWeights?.light}
                  color="primary.dark"
                  mt={2}
                  mb={2}
                >
                  <small>*Mensualidad estimada con tasa ponderada promedio.</small>
                </Typography>

                <Box display="flex" justifyContent="center" mb={2}>
                  <Button variant="default" onClick={continueToTOFU}>
                    Continuar
                  </Button>
                </Box>
              </Box>
            </Container>
          )}
        </Container>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default CotizadorGeneric;
